import React, { createContext, useContext, useState, ReactNode } from 'react'
import { Player } from '../game/GameEngine'

interface GameContextType {
  playerLevel: number
  playerXP: number
  playerWins: number
  playerLosses: number
  unlockedBuffs: string[]
  updatePlayerStats: (wins: number, losses: number, xp: number) => void
  levelUp: () => void
}

// AGC: Game context for managing player progression and stats
// AGC: Handles leveling, XP, win/loss tracking, and unlockables
const GameContext = createContext<GameContextType | undefined>(undefined)

interface GameProviderProps {
  children: ReactNode
}

// AGC: Game provider component for Origami Battles
// AGC: Manages global game state and player progression
export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [playerLevel, setPlayerLevel] = useState(1)
  const [playerXP, setPlayerXP] = useState(0)
  const [playerWins, setPlayerWins] = useState(0)
  const [playerLosses, setPlayerLosses] = useState(0)
  const [unlockedBuffs, setUnlockedBuffs] = useState<string[]>(['SHIELD'])

  // AGC: XP required per level (exponential scaling)
  const getXPRequiredForLevel = (level: number): number => {
    return level * 100 + (level - 1) * 50
  }

  // AGC: Update player statistics after battle
  const updatePlayerStats = (wins: number, losses: number, xp: number) => {
    setPlayerWins(prev => prev + wins)
    setPlayerLosses(prev => prev + losses)
    setPlayerXP(prev => {
      const newXP = prev + xp
      
      // AGC: Check for level up
      const xpRequired = getXPRequiredForLevel(playerLevel + 1)
      if (newXP >= xpRequired) {
        levelUp()
      }
      
      return newXP
    })
  }

  // AGC: Handle player level up
  const levelUp = () => {
    setPlayerLevel(prev => {
      const newLevel = prev + 1
      
      // AGC: Unlock new buffs at certain levels
      if (newLevel === 3 && !unlockedBuffs.includes('HASTE')) {
        setUnlockedBuffs(prev => [...prev, 'HASTE'])
      }
      if (newLevel === 5 && !unlockedBuffs.includes('PIERCE')) {
        setUnlockedBuffs(prev => [...prev, 'PIERCE'])
      }
      if (newLevel === 8 && !unlockedBuffs.includes('FROST')) {
        setUnlockedBuffs(prev => [...prev, 'FROST'])
      }
      
      return newLevel
    })
    
    // AGC: Reset XP for next level
    setPlayerXP(0)
  }

  const value: GameContextType = {
    playerLevel,
    playerXP,
    playerWins,
    playerLosses,
    unlockedBuffs,
    updatePlayerStats,
    levelUp
  }

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  )
}

// AGC: Custom hook for accessing game context
export const useGame = () => {
  const context = useContext(GameContext)
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider')
  }
  return context
}
